import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, FileDown, Users, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { rs, todayIso, monthOf, fmtMonth, fmtDate } from "@/lib/format";
import { PageHeader, Panel, KpiCard } from "@/components/Kpi";
import { exportSalarySlipPdf, exportSalarySummaryPdf } from "@/lib/salaryPdf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const EMPTY_STAFF = { name: "", role: "", monthly_salary: 0, opening_month: "", opening_balance: 0, active: true };
const FIELDS = [
  ["name", "Staff name", "text"], ["role", "Role (e.g. Chef)", "text"],
  ["monthly_salary", "Fixed monthly salary (₹)", "number"],
  ["opening_month", "Tracking starts from", "month"], ["opening_balance", "Opening balance (₹)", "number"],
];

const StaffDialog = ({ value, onClose, onSaved }) => {
  const [f, setF] = useState(value);
  useEffect(() => setF(value), [value]);
  if (!f) return null;
  const save = async () => {
    try {
      const body = { ...f, monthly_salary: Number(f.monthly_salary) || 0, opening_balance: Number(f.opening_balance) || 0 };
      if (f.id) await api.put(`/staff/${f.id}`, body); else await api.post("/staff", body);
      toast.success("Staff saved"); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="staff-dialog">
        <DialogHeader><DialogTitle>{f.id ? `Edit ${f.name}` : "Add staff"}</DialogTitle>
          <DialogDescription>Name, role and fixed monthly salary. Salary cycle is 1st to month end.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          {FIELDS.map(([k, l, type]) => (
            <div key={k} className={`space-y-1.5 ${k === "name" ? "col-span-2" : ""}`}>
              <Label className="text-xs">{l}</Label>
              <Input type={type} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`staff-${k}-input`} />
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500">Opening balance: positive = pending salary we owe them, negative = advance already taken. After that every month carries forward automatically.</p>
        <DialogFooter><Button onClick={save} disabled={!f.name?.trim()} className="bg-amber-600 hover:bg-amber-700" data-testid="staff-save-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Line = ({ l, v, strong, tone = "" }) => (
  <div className={`flex justify-between py-1.5 ${strong ? "border-t border-slate-200 mt-1 pt-2.5 font-semibold text-slate-900" : "text-slate-600"} ${tone}`}>
    <span>{l}</span><span className="num">{v}</span>
  </div>
);

const Detail = ({ row, month, business, onChanged }) => {
  const [leave, setLeave] = useState(String(row.leave_days));
  const [salary, setSalary] = useState(String(row.salary));
  const [f, setF] = useState({ date: `${month}-01`, item: "Cash", amount: "", payer: "", note: "" });
  const [payers, setPayers] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => { const p = r.data.payers || []; setPayers(p); setF((x) => ({ ...x, payer: x.payer || p[0] || "" })); }); }, []);
  useEffect(() => { setLeave(String(row.leave_days)); setSalary(String(row.salary)); setF((x) => ({ ...x, date: `${month}-01` })); }, [row.staff_id, month, row.leave_days, row.salary]);

  const saveMonth = async () => {
    setBusy(true);
    try { await api.put(`/salary/${month}/${row.staff_id}`, { leave_days: Number(leave) || 0, salary: salary === "" ? null : Number(salary) }); toast.success("Leave & salary updated"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const add = async () => {
    setBusy(true);
    try {
      await api.post("/salary-entries", { staff_id: row.staff_id, date: f.date, item: f.item, amount: Number(f.amount) || 0, payer: f.payer, note: f.note });
      setF({ ...f, amount: "", note: "" }); toast.success("Payment added"); onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const del = async (e) => {
    if (!window.confirm(`Delete ${e.item} ₹${e.amount} on ${fmtDate(e.date)}?`)) return;
    await api.delete(`/salary-entries/${e.id}`); onChanged();
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6" data-testid="staff-detail">
      <Panel className="xl:col-span-2" testId="salary-entries-panel"
        title={`${row.name} — payments in ${fmtMonth(month)}`}
        right={<Button size="sm" variant="outline" onClick={() => exportSalarySlipPdf(row, month, business)} data-testid="staff-slip-pdf-button"><FileDown className="h-4 w-4 mr-1" />Salary PDF</Button>}>
        <div className="flex flex-wrap items-end gap-2 mb-4 rounded-lg bg-amber-50/60 border border-amber-200 p-3">
          <div className="space-y-1"><Label className="text-[11px]">Date</Label>
            <Input type="date" value={f.date} className="h-9 w-40 bg-white" onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="salary-entry-date-input" /></div>
          <div className="space-y-1"><Label className="text-[11px]">Item (anything)</Label>
            <Input value={f.item} placeholder="Cash / Daru / Chef Recharge" className="h-9 w-48 bg-white" onChange={(e) => setF({ ...f, item: e.target.value })} data-testid="salary-entry-item-input" /></div>
          <div className="space-y-1"><Label className="text-[11px]">Amount ₹</Label>
            <Input type="number" value={f.amount} placeholder="0" className="h-9 w-28 bg-white num text-right" onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="salary-entry-amount-input" /></div>
          <div className="space-y-1"><Label className="text-[11px]">Paid by</Label>
            <select value={f.payer} onChange={(e) => setF({ ...f, payer: e.target.value })} data-testid="salary-entry-payer-select"
              className="h-9 w-32 rounded-md border border-slate-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500">
              <option value="">— none —</option>
              {payers.map((p) => <option key={p} value={p}>{p}</option>)}
            </select></div>
          <div className="space-y-1 flex-1 min-w-[120px]"><Label className="text-[11px]">Note</Label>
            <Input value={f.note} placeholder="optional" className="h-9 bg-white" onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="salary-entry-note-input" /></div>
          <Button size="sm" onClick={add} disabled={busy || !f.item.trim() || !f.amount} className="bg-amber-600 hover:bg-amber-700 h-9" data-testid="salary-entry-add-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </Button>
        </div>
        <p className="text-xs text-slate-500 mb-2">Use a minus amount for a discount / write-off (e.g. −500 discount on daru).</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="salary-entries-table">
            <thead className="border-b"><tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="py-2 pr-3">Date</th><th className="py-2 pr-3">Item</th><th className="py-2 pr-3 text-right">Amount</th><th className="py-2 pr-3">Note</th><th /></tr></thead>
            <tbody>
              {row.entries.map((e) => (
                <tr key={e.id} className="border-b border-slate-100 hover:bg-amber-50/30" data-testid={`salary-entry-row-${e.id}`}>
                  <td className="py-2 pr-3 text-slate-500 whitespace-nowrap">{fmtDate(e.date, { day: "numeric", month: "short" })}</td>
                  <td className="py-2 pr-3 font-medium text-slate-800">{e.item}</td>
                  <td className={`py-2 pr-3 text-right num font-semibold ${e.amount < 0 ? "text-emerald-700" : "text-slate-800"}`}>{rs(e.amount)}</td>
                  <td className="py-2 pr-3 text-xs text-slate-500">{[e.payer && `${e.payer} paid`, e.note].filter(Boolean).join(" · ")}</td>
                  <td className="py-2 text-right"><button onClick={() => del(e)} className="p-1.5 text-slate-300 hover:text-rose-600" data-testid={`delete-salary-entry-${e.id}`}><Trash2 className="h-4 w-4" /></button></td>
                </tr>
              ))}
              {!row.entries.length && <tr><td colSpan={5} className="py-6 text-center text-slate-400">No payments recorded for this month yet.</td></tr>}
            </tbody>
            <tfoot><tr className="bg-amber-50 font-bold text-amber-900"><td className="py-2 pr-3" colSpan={2}>TOTAL PAID</td>
              <td className="py-2 pr-3 text-right num" data-testid="staff-total-paid">{rs(row.paid)}</td><td colSpan={2} /></tr></tfoot>
          </table>
        </div>
      </Panel>
      <div className="space-y-6">
        <Panel title="Leave & salary this month" testId="salary-month-panel">
          <div className="flex items-end gap-2">
            <div className="space-y-1"><Label className="text-[11px]">Leave days</Label>
              <Input type="number" min="0" step="0.5" value={leave} className="h-9 w-24 num text-right" onChange={(e) => setLeave(e.target.value)} data-testid="leave-days-input" /></div>
            <div className="space-y-1 flex-1"><Label className="text-[11px]">Salary for {fmtMonth(month)}</Label>
              <Input type="number" value={salary} className="h-9 num text-right" onChange={(e) => setSalary(e.target.value)} data-testid="month-salary-input" /></div>
            <Button size="sm" className="h-9" onClick={saveMonth} disabled={busy} data-testid="save-salary-month-button"><Save className="h-4 w-4" /></Button>
          </div>
          <div className="mt-4 text-sm" data-testid="staff-summary">
            <Line l={`Daily rate (${row.days_in_month} days)`} v={rs(row.daily_rate)} />
            <Line l="Fixed salary" v={rs(row.salary)} />
            <Line l={`Less: ${row.leave_days} day leave`} v={`−${rs(row.leave_deduction)}`} tone="text-orange-700" />
            <Line l="Net salary" v={rs(row.net_salary)} strong />
            <Line l="Previous balance (carried)" v={rs(row.previous_balance)} tone={row.previous_balance < 0 ? "text-emerald-700" : "text-sky-800"} />
            <Line l="Total payable" v={rs(row.payable)} strong />
            <Line l="Already paid" v={`−${rs(row.paid)}`} tone="text-rose-700" />
            <div className={`flex justify-between border-t-2 mt-1 pt-2.5 font-bold ${row.balance >= 0 ? "text-sky-900" : "text-rose-700"}`}>
              <span>{row.balance >= 0 ? `Payable to ${row.name}` : `Due from ${row.name}`}</span>
              <span className="num" data-testid="staff-balance">{rs(Math.abs(row.balance))}</span>
            </div>
          </div>
        </Panel>
      </div>
    </div>
  );
};

export default function StaffSalary() {
  const [month, setMonth] = useState(monthOf(todayIso()));
  const [data, setData] = useState(null);
  const [master, setMaster] = useState([]);
  const [sel, setSel] = useState(null);
  const [edit, setEdit] = useState(null);

  const load = (keep = sel) => Promise.all([api.get(`/salary/${month}`), api.get("/staff")]).then(([r, m]) => {
    setData(r.data); setMaster(m.data);
    const rows = r.data.staff;
    setSel(rows.find((x) => x.staff_id === keep) ? keep : rows[0]?.staff_id || null);
  }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { setData(null); load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  const del = async (r) => {
    if (!window.confirm(`Remove ${r.name} from staff?`)) return;
    const res = await api.delete(`/staff/${r.staff_id}`);
    toast.success(res.data.archived ? "Staff archived (payment history kept)" : "Staff removed"); load(null);
  };

  const rows = data?.staff || [];
  const t = data?.totals;
  const selected = rows.find((r) => r.staff_id === sel);

  return (
    <div>
      <PageHeader title="Staff Salary" subtitle="Monthly salary, leave, date-wise payments (cash / daru / recharge) and auto carry-forward balance.">
        <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="w-44 bg-white" data-testid="salary-month-picker" />
        <Button variant="outline" onClick={() => setEdit({ ...EMPTY_STAFF, opening_month: month })} data-testid="add-staff-button"><Plus className="h-4 w-4 mr-1" />Add staff</Button>
        <Button onClick={() => data && exportSalarySummaryPdf(data)} disabled={!rows.length} className="bg-amber-600 hover:bg-amber-700" data-testid="salary-summary-pdf-button">
          <FileDown className="h-4 w-4 mr-1" />All staff PDF
        </Button>
      </PageHeader>

      {t && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiCard label="Staff on roll" value={t.count} icon={Users} tone="slate" testId="kpi-staff-count" />
          <KpiCard label="Net salary (month)" value={rs(t.net_salary)} sub={`Leave: ${t.leave_days} days`} tone="amber" testId="kpi-net-salary" delay={60} />
          <KpiCard label="Paid this month" value={rs(t.paid)} tone="emerald" testId="kpi-salary-paid" delay={120} />
          <KpiCard label="Balance outstanding" value={rs(t.balance)} sub={`Incl. previous ${rs(t.previous_balance)}`} tone="rose" testId="kpi-salary-balance" delay={180} />
        </div>
      )}

      <Panel title={`Staff — ${fmtMonth(month)}`} className="mb-6" testId="staff-list-panel">
        {!data ? <div className="text-slate-500">Loading…</div> : !rows.length ? (
          <div className="py-8 text-center text-slate-500" data-testid="staff-empty-state">
            No staff yet. Click <span className="font-semibold">Add staff</span> to add your first person (name + fixed monthly salary).
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="staff-table">
              <thead className="border-b"><tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="py-2 pr-3">Staff</th><th className="py-2 pr-3">Role</th>
                <th className="py-2 pr-3 text-right">Salary</th><th className="py-2 pr-3 text-right">Leave</th>
                <th className="py-2 pr-3 text-right">Net</th><th className="py-2 pr-3 text-right">Prev. bal.</th>
                <th className="py-2 pr-3 text-right">Paid</th><th className="py-2 pr-3 text-right">Balance</th><th /></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.staff_id} onClick={() => setSel(r.staff_id)} data-testid={`staff-row-${r.staff_id}`}
                    className={`border-b border-slate-100 cursor-pointer transition-colors ${sel === r.staff_id ? "bg-amber-50" : "hover:bg-amber-50/30"} ${r.active ? "" : "opacity-50"}`}>
                    <td className="py-2.5 pr-3 font-medium text-slate-800">{r.name}{!r.active && <span className="ml-2 text-[10px] uppercase text-slate-400">archived</span>}</td>
                    <td className="py-2.5 pr-3 text-slate-500">{r.role || "—"}</td>
                    <td className="py-2.5 pr-3 text-right num">{rs(r.salary)}</td>
                    <td className="py-2.5 pr-3 text-right num text-orange-700">{r.leave_days || "—"}</td>
                    <td className="py-2.5 pr-3 text-right num font-semibold">{rs(r.net_salary)}</td>
                    <td className="py-2.5 pr-3 text-right num text-slate-500">{rs(r.previous_balance)}</td>
                    <td className="py-2.5 pr-3 text-right num text-emerald-700">{rs(r.paid)}</td>
                    <td className={`py-2.5 pr-3 text-right num font-bold ${r.balance >= 0 ? "text-sky-900" : "text-rose-700"}`} data-testid={`staff-balance-${r.staff_id}`}>{rs(r.balance)}</td>
                    <td className="py-2.5 text-right whitespace-nowrap">
                      <button onClick={(ev) => { ev.stopPropagation(); exportSalarySlipPdf(r, month, data.business_name); }} className="p-1.5 text-slate-400 hover:text-amber-700" data-testid={`staff-pdf-${r.staff_id}`}><FileDown className="h-4 w-4" /></button>
                      <button onClick={(ev) => { ev.stopPropagation(); setEdit(master.find((m) => m.id === r.staff_id)); }} className="p-1.5 text-slate-400 hover:text-amber-700" data-testid={`edit-staff-${r.staff_id}`}><Pencil className="h-4 w-4" /></button>
                      <button onClick={(ev) => { ev.stopPropagation(); del(r); }} className="p-1.5 text-slate-400 hover:text-rose-600" data-testid={`delete-staff-${r.staff_id}`}><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className="bg-slate-50 font-bold text-slate-800">
                <td className="py-2.5 pr-3" colSpan={2}>TOTAL</td>
                <td className="py-2.5 pr-3 text-right num">{rs(t.salary)}</td><td className="py-2.5 pr-3 text-right num">{t.leave_days || "—"}</td>
                <td className="py-2.5 pr-3 text-right num">{rs(t.net_salary)}</td><td className="py-2.5 pr-3 text-right num">{rs(t.previous_balance)}</td>
                <td className="py-2.5 pr-3 text-right num" data-testid="salary-total-paid">{rs(t.paid)}</td>
                <td className="py-2.5 pr-3 text-right num" data-testid="salary-total-balance">{rs(t.balance)}</td><td /></tr></tfoot>
            </table>
          </div>
        )}
      </Panel>

      {selected && <Detail row={selected} month={month} business={data.business_name} onChanged={() => load(selected.staff_id)} />}
      {edit && <StaffDialog value={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
